<template>
    <section class="traineeship-required-card card mb-3">
        <form
            ref="traineeshipRequiredForm"
            class="card-body"
            v-click-outside="onClickOutside"
            @click="focusBlock"
        >
            <div class="card-heading">
                <h5 class="card-name">
                    {{ $t('lib_traineeship_study_title') }}
                </h5>
            </div>
            <hr />
            <p
                class="block-error-feedback text-danger"
                v-if="blockErrorMessage(block.name)"
            >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name)) }}
            </p>

            <div>
                <div class="form-group row">
                    
                    <label 
                        class="traineeshipRequired-text mb-2" 
                        for="traineeshipMandatoryForStudies"
                    > {{ $t('lib_traineeship_study_question') }} *
                        <span
                            v-tooltip.top.hover.focus="$t('lib_traineeship_study_question_info')"
                        >
                            <font-awesome-icon icon="far fa-question-circle" />
                        </span>
                    </label>
                    <div class="options-wrapper">

                        <div class="option-yes">
                            <RadioButton 
                                v-model="traineeshipRequired.isTraineeshipMandatoryForStudies" 
                                inputId="traineeshipYes" 
                                name="traineeshipYes" 
                                :value="true" 
                                @update:modelValue="formWasChanged = true"
                                variant="filled"
                                binary
                            />                        
                            <label for="traineeshipYes" class="ml-2">{{ $t('lib_yes') }}</label>
                        </div>

                        <div class="option-no">
                            <RadioButton 
                                v-model="traineeshipRequired.isTraineeshipMandatoryForStudies" 
                                inputId="traineeshipNo" 
                                name="traineeshipNo" 
                                :value="false" 
                                @update:modelValue="formWasChanged = true"
                                variant="filled"
                                binary
                            />                        
                            <label for="traineeshipNo" class="ml-2">{{ $t('lib_no') }}</label>
                        </div>
                        
                        
                        
                    </div>
                        
                </div>
            </div>
            <div class="card-footer d-flex justify-content-end mt-3" v-if="formWasChanged">
                <div class="card-actions">
                    <button
                        type="button"
                        class="card-action save-action btn btn-text me-2"
                        @click="validate()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="far fa-save"  class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="card-action undo-changes-action btn btn-text"
                        @click="undoChanges()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="fas fa-undo"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import RadioButton from 'primevue/radiobutton';
import _ from 'lodash';

export default {
    name: 'TraineeshipRequired',
    components: {
        RadioButton,
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            traineeshipRequired: {},
            options: [
                { name: this.$t('lib_yes'), value: true },
                { name: this.$t('lib_no'), value: false },
            ],
            listenToClickOutside: false,
            formWasChanged: false,
        };
    },
    computed: {
        ...mapGetters([
            'blockErrorMessage',
            'errorMessage',
            'traineeshipRequiredData',
            'isFieldRequired',
            'appLoading',
            'applicationTypeIsShort',
        ]),
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);

            if (this.traineeshipRequired.isTraineeshipMandatoryForStudies === null){
                this.traineeshipRequiredData.isValid = false;

                this.$toast.add({
                    severity: 'error',
                    detail: this.$t('mess_save_failed'),
                    life: 3000,
                });
            }
            this.validateBlock({
                blockData: this.traineeshipRequired,
                blockName: this.block.name,
            }).then(
                () => {
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: this.traineeshipRequired,
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();

                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.traineeshipRequired = _.cloneDeep(this.traineeshipRequiredData);
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'traineeshipRequired',
                label: this.$t('lib_traineeship_study_title'),
            });
        },
    },
    mounted() {
        this.loadData();
    },
    watch: {
        traineeshipRequired: {
            handler(newData, oldData) {
                this.formWasChanged =
                    this.traineeshipRequired.isTraineeshipMandatoryForStudies !== this.traineeshipRequiredData.isTraineeshipMandatoryForStudies;

                if (!this.formWasChanged && oldData) {
                    // checking for oldData to prevent clearing the errors on firstload;
                    this.clearBlockErrors(this.block.name);
                }
            },
            deep: true,
        }
    }
};
</script>